import React, { useEffect, useMemo, useState } from 'react';
import { Col, Flex, Row, Tooltip } from 'antd';
import { faLightbulb, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { CanonicalTopic, Study, StudyClient, StudyScoreClient, StudySequenceClient } from 'jf/api';
import { DevExPageLayout } from 'jf/common/DevExPageLayout';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExAISummaryCard } from 'jf/components/DevExAISummaryCard';
import { DevExBadge } from 'jf/components/DevExBadge';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExSkeleton } from 'jf/components/DevExSkeleton';
import { DevExText } from 'jf/components/DevExText';
import { TopicSelect } from 'jf/pages/studyBuilder/customizeStudy/TopicSelect';
import { useClientPerms } from 'jf/utils/useClientPerms';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';
import { usePageStorage } from 'jf/utils/usePageStorage';

import { BenchmarkPercentile, BenchmarkSelect } from '../benchmark/BenchmarkSelect';
import { BenchmarkCell } from '../insights/BenchmarkCell';
import { TrendCell } from '../insights/TrendCell';
import { ScoreBlock } from '../ScoreBlock';
import { StarredTopicStar } from '../StarredTopicStar';
import { makeStudyAnalyzerPath } from '../StudyAnalyzerPage';
import { TeamCascader } from '../TeamCascader';
import { useCanViewSurveyResults } from '../useCanViewSurveyResults';

import { PromptScoreCard, PromptTextResponseCard } from './PromptScoreCard';
import { MetricTopicSlug, TopicMetrics } from './TopicMetrics';
import { useSortedTextTopicData } from './useSortedTextTopicData';
import { TopicDataRecord, useTopicData } from './useTopicData';

const TopicRecommendedAction = ({
  topic,
  studyRef,
  topicSlug,
}: {
  topic: CanonicalTopic;
  studyRef: string | undefined;
  topicSlug: string;
}) => {
  const theme = useDevExTheme();
  const queryClient = useQueryClient();

  const perms = useClientPerms();
  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });
  const createStarredTopic = useClientMutation(StudySequenceClient.createStarredTopic);
  const createActionItemMutation = useClientMutation(
    StudySequenceClient.createStarredTopicActionItem
  );
  const showStarredTopics = perms.role.customerAdmin && !!study?.sequenceRef;
  const onAddActionItem = async (action: string) => {
    if (study?.sequenceRef && topicSlug) {
      trackEvent('survey-analyzer:topic:accept-action-item-recommendation', {
        topicSlug,
        text: action,
      });

      // create/activate StarredTopic
      await createStarredTopic.mutateAsync({
        sequenceRef: study.sequenceRef,
        requestBody: { topicSlug },
      });

      // create StarredTopicActionItem
      createActionItemMutation
        .mutateAsync({
          sequenceRef: study.sequenceRef!,
          topicSlug,
          requestBody: {
            text: action,
            isRecommendation: true,
          },
        })
        .then(() => {
          queryClient.invalidateQueries('GET_STARRED_TOPICS');
        });
    }
  };
  return (
    <DevExCard
      style={{
        background: theme.color.status.discovery.background,
        borderColor: theme.color.status.discovery.border,
      }}
    >
      <Flex
        style={{ color: theme.color.status.discovery.text, fontWeight: 600 }}
        gap={theme.variable.spacing.sm}
      >
        <FontAwesomeIcon icon={faLightbulb} />
        Recommended Actions
      </Flex>
      <ul
        style={{
          margin: 0,
          paddingInline: theme.variable.spacing.lg,
          lineHeight: theme.variable.lineHeight,
        }}
      >
        {topic.actions.map((action, i) => (
          <li key={i}>
            <Flex justify="space-between" align="center" gap={theme.variable.spacing.sm}>
              {action}
              {showStarredTopics && (
                <Tooltip title={`Add action item for ${topic.label}`}>
                  <DevExButton
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    size="small"
                    type="text"
                    onClick={() => onAddActionItem(action)}
                    disabled={createActionItemMutation.isLoading}
                  />
                </Tooltip>
              )}
            </Flex>
          </li>
        ))}
      </ul>
    </DevExCard>
  );
};

const TopicPrioritySection = ({ topicDatum }: { topicDatum: TopicDataRecord }) => {
  const theme = useDevExTheme();
  return (
    <Flex vertical gap={theme.variable.spacing.sm}>
      <DevExText type="secondary" strong loading={!topicDatum}>
        Priority
      </DevExText>
      <Flex align="center" gap={theme.variable.spacing.md}>
        <span style={{ fontSize: theme.variable.fontSize.lg, fontWeight: '600' }}>
          #{topicDatum.priority}
        </span>
        <div style={{ lineHeight: 'normal' }}>
          <span style={{ fontWeight: '600' }}>
            {topicDatum?.votes.length} respondent
            {topicDatum?.votes.length !== 1 ? 's' : ''}{' '}
          </span>
          voted {topicDatum?.topic.label} a top priority when asked "What topics would you like to
          see prioritized"{' '}
        </div>
      </Flex>
    </Flex>
  );
};

export const TopicsTab: React.FC = () => {
  const { studyRef, teamRef } = useParams<{ studyRef: string; teamRef: string }>();
  const navigate = useNavigate();
  const theme = useDevExTheme();
  const perms = useClientPerms();

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef });
  const isClosed = study?.status === Study.status.CLOSED;
  const shouldShowSurveyResults = useCanViewSurveyResults({ studyRef });
  const [topicSlug, setTopicSlug] = useState<string>();

  const { pageStateLoaded } = usePageStorage(
    { topic: [topicSlug, setTopicSlug] },
    'devex_survey_analyzer_topic'
  );

  const [benchmarkPercentile, setBenchmarkPercentile] = useState<BenchmarkPercentile>();

  const prioritizedTopicData = useTopicData({
    studyRef,
    teamRef,
    benchmarkPercentile,
    sortBy: 'priority',
  });

  const textTopicData = useSortedTextTopicData({
    studyRef,
  });

  // default to highest prio topic
  const prioritizedTopics = prioritizedTopicData?.map((topicData) => topicData.topic) || [];

  const openTextTopics = textTopicData?.map((topicData) => topicData.topic) || [];

  const allTopics = Array.from(new Set([...prioritizedTopics, ...openTextTopics]));
  useEffect(() => {
    const changeTopic =
      !topicSlug ||
      (!!prioritizedTopicData && allTopics.findIndex((t) => t.slug === topicSlug) === -1);
    if (pageStateLoaded && prioritizedTopicData && changeTopic) {
      setTopicSlug(prioritizedTopicData[0].topic.slug);
    }
  }, [pageStateLoaded, topicSlug, prioritizedTopicData]);

  const topicDatum = useMemo(() => {
    return prioritizedTopicData?.find((topicDatum) => topicDatum.topic.slug === topicSlug);
  }, [prioritizedTopicData, topicSlug]);

  const { data: commentSummary } = useClientQuery(
    StudyScoreClient.getStudyCommentSummary,
    {
      studyRef: studyRef,
      topicSlug: topicSlug,
    },
    {
      // only fetch if there are comments
      enabled: topicDatum && topicDatum.comments.length > 0,
    }
  );
  const topicOpenTextDatum = useMemo(() => {
    return textTopicData?.find((topicDatum) => topicDatum.topic.slug === topicSlug);
  }, [textTopicData, topicSlug]);

  const canShowMetrics = study && study.options.showMetrics;

  const hasGbacLimitedAccess = window.dx.user?.gbac.companyAccess === false;
  const showMetricCards = !!studyRef && topicSlug && !hasGbacLimitedAccess && canShowMetrics;

  const showBenchmarks = !!window.dx.user?.company.aggregatable && study && isClosed;

  const showStarredTopics = perms.role.customerAdmin && !!study?.sequenceRef;

  return (
    <DevExPageLayout>
      <Flex gap={theme.variable.spacing.sm}>
        {shouldShowSurveyResults && (
          <TeamCascader
            studyRef={studyRef}
            value={teamRef}
            onChange={(teamRef) => navigate(makeStudyAnalyzerPath(studyRef, teamRef, 'topics'))}
            allowClear
          />
        )}
        {showBenchmarks && (
          <BenchmarkSelect value={benchmarkPercentile} onChange={setBenchmarkPercentile} />
        )}
        <TopicSelect
          topics={allTopics}
          value={topicSlug}
          onChange={setTopicSlug}
          prefix={
            <>
              <FontAwesomeIcon icon={faFilter} />
              Topic
            </>
          }
        />
      </Flex>

      {topicDatum || topicOpenTextDatum ? (
        <Flex
          align="center"
          gap={theme.variable.spacing.sm}
          style={{ fontSize: theme.variable.fontSize.lg, fontWeight: '600' }}
        >
          {showStarredTopics && !!topicSlug && (
            <StarredTopicStar
              sequenceRef={study?.sequenceRef}
              topicSlug={topicSlug}
              eventName="survey-analyzer:topic:star-topic"
            />
          )}
          {topicDatum?.topic.label || topicOpenTextDatum?.topic.label}
        </Flex>
      ) : (
        <DevExSkeleton fontSize="lg" width={160} noLineHeight />
      )}

      <Row gutter={parseInt(theme.variable.spacing.lg)}>
        <Col span={14}>
          <Flex vertical gap={theme.variable.spacing.lg}>
            {topicDatum && <TopicPrioritySection topicDatum={topicDatum} />}

            <Flex vertical gap={theme.variable.spacing.sm}>
              <DevExText type="secondary" strong loading={!topicDatum && !topicOpenTextDatum}>
                Description
              </DevExText>
              {topicDatum || topicOpenTextDatum ? (
                <div style={{ lineHeight: theme.variable.lineHeight }}>
                  {topicDatum?.topic.longDescription || topicOpenTextDatum?.topic.longDescription}
                </div>
              ) : (
                <DevExSkeleton fontSize="sm" width={['100%', '100%', '50%']} />
              )}
            </Flex>

            {showMetricCards && (
              <TopicMetrics
                studyRef={studyRef}
                teamRef={teamRef}
                topicSlug={topicSlug as MetricTopicSlug}
              />
            )}

            <Flex vertical gap={theme.variable.spacing.sm}>
              <Flex gap={theme.variable.spacing.xs} align="baseline">
                <DevExText type="secondary" strong loading={!topicDatum && !topicOpenTextDatum}>
                  Survey Questions
                </DevExText>
                <DevExBadge
                  count={
                    (topicDatum ? topicDatum?.promptScores.length : 0) +
                    (textTopicData
                      ? textTopicData?.filter((topicDatum) => topicDatum.topic.slug === topicSlug)
                          ?.length
                      : 0)
                  }
                />
              </Flex>

              <Flex vertical gap={theme.variable.spacing.md}>
                {topicDatum && (
                  <>
                    {topicDatum?.promptScores.map((score) => (
                      <PromptScoreCard
                        key={score.slug}
                        score={score}
                        promptSlug={score.slug}
                        study={study}
                      />
                    ))}
                  </>
                )}

                {textTopicData
                  ?.filter((topicDatum) => topicDatum.topic.slug === topicSlug)
                  ?.map((textResponses) => (
                    <PromptTextResponseCard
                      key={textResponses.prompt.slug}
                      promptSlug={textResponses.prompt.slug}
                      studyRef={studyRef!}
                      topicSlug={topicSlug}
                      responses={textResponses.textResponses}
                    />
                  ))}
              </Flex>
            </Flex>
          </Flex>
        </Col>

        <Col span={10}>
          <Flex vertical gap={theme.variable.spacing.lg}>
            {topicDatum && (
              <Flex gap={theme.variable.spacing.md}>
                <Flex vertical gap={theme.variable.spacing.sm}>
                  <DevExText type="secondary" strong loading={!topicDatum} loadingWidth={60}>
                    Score
                  </DevExText>
                  {topicDatum ? (
                    <ScoreBlock score={topicDatum.score.scaledAvg} size="lg" />
                  ) : (
                    <DevExSkeleton width={48} height={48} />
                  )}
                </Flex>

                <Flex vertical gap={theme.variable.spacing.sm}>
                  <DevExText type="secondary" strong loading={!topicDatum} loadingWidth={60}>
                    Trend
                  </DevExText>
                  {topicDatum && (
                    <Flex vertical justify="center" style={{ height: '100%' }}>
                      <TrendCell
                        trend={topicDatum.scoreTrend}
                        style={{ fontSize: theme.variable.fontSize.lg }}
                      />
                    </Flex>
                  )}
                </Flex>

                <Flex vertical gap={theme.variable.spacing.sm}>
                  <DevExText type="secondary" strong loading={!topicDatum} loadingWidth={80}>
                    Benchmark
                  </DevExText>
                  {topicDatum && (
                    <Flex vertical justify="center" style={{ height: '100%' }}>
                      <BenchmarkCell
                        benchmark={topicDatum.calculatedBenchmark}
                        style={{ fontSize: theme.variable.fontSize.lg }}
                      />
                    </Flex>
                  )}
                </Flex>
              </Flex>
            )}
            {topicDatum || topicOpenTextDatum ? (
              <TopicRecommendedAction
                topic={(topicDatum?.topic || topicOpenTextDatum?.topic) as CanonicalTopic}
                studyRef={studyRef}
                topicSlug={topicSlug as string}
              />
            ) : (
              <DevExSkeleton height={150} />
            )}
            {topicDatum?.comments && (
              <DevExAISummaryCard
                summary={commentSummary}
                items={topicDatum?.comments}
                topicSlug={topicSlug}
                eventType="comments"
                title="Comments"
              />
            )}
          </Flex>
        </Col>
      </Row>
    </DevExPageLayout>
  );
};
